"use client";

import type { FaqItem } from "@/components/landing/Faq/types";
import { faChevronDown, faCommentDots } from "@fortawesome/pro-solid-svg-icons";
import { MotionReveal, SectionHeader, useSectionMotion } from "@/components/landing/shared";
import { Icon } from "@/components/ui/Icon";
import { jsonLd } from "@/lib/seo";

interface FaqInnerProps {
	title: string;
	subtitle: string;
	items: FaqItem[];
}

function FaqInner(prop: FaqInnerProps) {
	const { title, subtitle, items } = prop;
	const motion = useSectionMotion();
	const faqJsonLd = {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: items.map((item) => ({
			"@type": "Question",
			name: item.q,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.a,
			},
		})),
	};

	return (
		<>
			<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(faqJsonLd) }} />
			<div className="mx-auto max-w-5xl px-6">
				<MotionReveal motion={motion}>
					<SectionHeader
						title={title}
						subtitle={subtitle || undefined}
						wrapperClassName="mb-10 max-w-md sm:mb-12"
						titleClassName="text-2xl font-bold tracking-tight sm:text-3xl"
						subtitleClassName="mt-2 leading-relaxed text-muted-foreground"
					/>
				</MotionReveal>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					{items.map((item, i) => (
						<MotionReveal motion={motion} delayOffset={(i % 6) * 50} key={item.q}>
							<details className="group rounded-2xl border border-(--border-subtle) bg-surface p-4 sm:p-5">
								<summary className="flex cursor-pointer list-none items-center gap-3">
									<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-(--background-teal-subtle) text-(--brand-1)">
										<Icon icon={faCommentDots} size={16} />
									</span>
									<span className="flex-1 text-sm font-medium sm:text-[15px]">{item.q}</span>
									<span className="shrink-0 text-muted-foreground transition-transform duration-300 group-open:rotate-180 group-open:text-(--brand-1)">
										<Icon icon={faChevronDown} size={16} />
									</span>
								</summary>
								<p className="mt-3 ps-12 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
							</details>
						</MotionReveal>
					))}
				</div>
			</div>
		</>
	);
}

export default FaqInner;
